import React, { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ReroutingNumber } from '../app/types'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import TextInput from '../common/form/TextInput'
import FormRow from '../common/form/FormRow'
import FormFieldBox from '../common/form/FormFieldBox'
import SpinnableSubmitButton from '../common/form/SpinnableSubmitButton'
import { useFormSubmission } from '../common/servercall/useFormSubmission'
import ErrorAlert from '../common/components/ErrorAlert'
import { UseFormSetValue } from 'react-hook-form/dist/types/form'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers'
import nlLocale from 'date-fns/locale/nl-BE'
import frLocale from 'date-fns/locale/fr'
import enLocale from 'date-fns/locale/en-GB'

type ActiveWindow = {
    activeFrom?: Date | null
    activeTo?: Date | null
}

type Props = {
    number: ReroutingNumber | null
    onSubmit: (formData: ReroutingNumber) => void,
    allowInternationalCallForwarding: boolean
}

export type ReturnProps = {
    handleSubmit: () => void
    form: () => ReactElement
    submitButton: () => ReactElement
    reset: () => void
    setValue: UseFormSetValue<ReroutingNumber>
}

export const localeMap = {
    nl: nlLocale,
    fr: frLocale,
    en: enLocale,
}

const useReroutingNumberForm = ({ number, onSubmit, allowInternationalCallForwarding }: Props): ReturnProps => {
    const { t, i18n } = useTranslation()

    const isActive = ({ activeFrom, activeTo }: ActiveWindow): boolean => {
        const now = new Date()
        if (!activeFrom && !activeTo) return false
        return (!activeFrom || activeFrom <= now) && (!activeTo || activeTo >= now)
    }

    const isInternationalNumber = (number: string | undefined): boolean => {
        if (!number) return false
        return (number.startsWith('+') && !number.startsWith('+32')) || (number.startsWith('00') && !number.startsWith('0032'))
    }

    const schema = yup
        .object()
        .shape({
            destinationNumber: yup
                .string()
                .trim()
                .matches(/^$|^\+[1-9]\d{2,14}$|^00\d{3,15}|^0[0-9]{8,9}$/, 'invalid-entry-number')
                .when(['activeFrom', 'activeTo'], {
                    is: (activeFrom: Date | null | undefined, activeTo: Date | null | undefined) =>
                        isActive({ activeFrom: activeFrom ?? null, activeTo: activeTo ?? null }),
                    then: s => s.required('rerouting.destination-required-when-active'),
                    otherwise: s => s.notRequired(),
                })
                .test('international-not-allowed', 'rerouting.international-not-allowed', (number) => allowInternationalCallForwarding || !isInternationalNumber(number))
                .nullable(),
            activeFrom: yup.date().typeError('invalid-date').nullable(),
            activeTo: yup
                .date()
                .typeError('invalid-date')
                .min(yup.ref('activeFrom'), 'rerouting.active-to-before-active-from')
                .nullable(),
            alias: yup.string().trim().max(100, 'too-long').nullable(),
            comment: yup.string().trim().max(100, 'too-long').nullable(),
        })
        .required()

    const { control, handleSubmit, setError, reset, setValue } = useForm<ReroutingNumber>({
        resolver: yupResolver(schema),
        defaultValues: { uuid: number?.uuid, number: number?.number },
    })

    const { submit, isSubmitting, serverError, onError } = useFormSubmission<ReroutingNumber>(onSubmit, setError)

    // @ts-ignore
    const dateFnsLocale = useMemo(() => localeMap[i18n.language], [i18n.language])

    return {
        setValue: setValue,
        reset: reset,
        handleSubmit: handleSubmit(submit, onError),
        form: () =>
            number ? (
                <>
                    <ErrorAlert errorKey={typeof serverError === 'string' ? serverError : undefined} showAlert={!!serverError} />
                    <FormRow>
                        <FormFieldBox fullWidth={true}>
                            <TextInput
                                autofocus={true}
                                label={t('rerouting.number.table.destination')}
                                name={'destinationNumber'}
                                control={control}
                                required={isActive({ activeFrom: number.activeFrom, activeTo: number.activeTo })}
                            />
                        </FormFieldBox>
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateFnsLocale}>
                            <FormFieldBox fullWidth={false}>
                                <TextInput label={t('rerouting.number.table.active-from')} name={'activeFrom'} control={control} required={false} isDateTime={true} />
                            </FormFieldBox>
                            <FormFieldBox fullWidth={false}>
                                <TextInput label={t('rerouting.number.table.active-to')} name={'activeTo'} control={control} required={false} isDateTime={true} />
                            </FormFieldBox>
                        </LocalizationProvider>
                        <FormFieldBox fullWidth={true}>
                            <TextInput label={t('rerouting.number.table.alias')} name={'alias'} control={control} required={false} />
                        </FormFieldBox>
                        <FormFieldBox fullWidth={true}>
                            <TextInput label={t('rerouting.number.table.comment')} name={'comment'} control={control} required={false} />
                        </FormFieldBox>
                    </FormRow>
                </>
            ) : (
                <div />
            ),
        submitButton: () => (number ? <SpinnableSubmitButton label={t(number.uuid ? 'general.form-update' : 'general.form-create')} showSpinner={isSubmitting} /> : <></>),
    }
}

export default useReroutingNumberForm
